import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { useContributions } from '../../hooks/useContributions';
import FamilyChain from '../../components/ui/FamilyChain';
import StatCard from '../../components/ui/StatCard';
import { fmt, fmtDate, initials } from '../../lib/constants';

export default function MemberHome() {
  const { member } = useAuth();
  const navigate   = useNavigate();
  const [word,     setWord]     = useState(null);
  const [notices,  setNotices]  = useState([]);

  const { contributions, summary } = useContributions(member ? { member_id: member.id, year: new Date().getFullYear() } : {});
  const groups = (member?.member_groups || []).map(g => g.groups?.name).filter(Boolean);

  useEffect(() => {
    supabase.from('word_of_week').select('*').eq('is_active',true).limit(1).then(({data})=>setWord(data?.[0]||null));
    supabase.from('notices').select('*').order('created_at',{ascending:false}).limit(3).then(({data})=>setNotices(data||[]));
  }, []);

  if (!member) return null;

  const completeness = [member.first_name,member.last_name,member.dob,member.gender,member.phone||member.email,member.district_id,member.membership_type].filter(Boolean).length;
  const pct = Math.round((completeness/7)*100);

  return (
    <div>
      {/* Welcome banner */}
      <div className="welcome-banner">
        <div style={{fontSize:13,opacity:.7,marginBottom:4}}>Welcome back,</div>
        <div className="welcome-name">{member.first_name} {member.last_name}</div>
        <div className="welcome-sub">{member.membership_type} · {member.districts?.name} District</div>
        <div className="welcome-tags">
          {groups.map(g=><span key={g} className="welcome-tag">✝ {g}</span>)}
          {member.families?.name && <span className="welcome-tag">🏠 {member.families.name}</span>}
        </div>
      </div>

      {/* Profile completion */}
      {pct < 100 && (
        <div className="alert alert-warn mb-4" style={{cursor:'pointer'}} onClick={()=>navigate('/member/details')}>
          📝 Your profile is <strong>{pct}% complete</strong>. Click to update your details.
        </div>
      )}

      {/* Stats */}
      <div className="grid-3 mb-4">
        <StatCard icon="💰" label="Total Contributions (YTD)"  value={fmt(summary.total||0)}    color="red" />
        <StatCard icon="🙏" label="Tithes (YTD)"               value={fmt(summary.tithes||0)}   color="blue" />
        <StatCard icon="🎁" label="Offerings & Other"          value={fmt((summary.offerings||0)+(summary.special||0))} color="green" />
      </div>

      <div className="grid-2 mb-4">
        {/* Profile summary */}
        <div className="card">
          <div className="card-header"><h3>My Profile</h3><button className="btn btn-outline btn-sm" onClick={()=>navigate('/member/details')}>Edit</button></div>
          <div className="card-pad">
            <div style={{display:'flex',gap:14,alignItems:'center',marginBottom:16}}>
              <div style={{width:52,height:52,borderRadius:'50%',background:'var(--red-light)',color:'var(--red-dark)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:20,fontWeight:700,flexShrink:0}}>
                {initials(member)}
              </div>
              <div>
                <div style={{fontSize:17,fontWeight:800}}>{member.first_name} {member.last_name}</div>
                <span className={`badge badge-${member.membership_type==='Full Member'?'blue':'gray'}`}>{member.membership_type}</span>
              </div>
            </div>
            {/* Profile completion bar */}
            <div style={{marginBottom:14}}>
              <div style={{display:'flex',justifyContent:'space-between',fontSize:11,color:'var(--text3)',marginBottom:4}}><span>Profile Completion</span><span>{pct}%</span></div>
              <div style={{height:6,background:'var(--bg)',borderRadius:3,overflow:'hidden'}}>
                <div style={{height:'100%',width:`${pct}%`,background:'var(--red)',borderRadius:3,transition:'width .4s'}} />
              </div>
            </div>
            {[['📍 District',member.districts?.name||'—'],['📞 Phone',member.phone||'—'],['✉️ Email',member.email||'—'],['🏘️ Area',member.area||'—'],['📅 Joined',fmtDate(member.joined_date)]].map(([l,v])=>(
              <div key={l} className="kv-row"><span style={{color:'var(--text3)'}}>{l}</span><span style={{fontWeight:600}}>{v}</span></div>
            ))}
          </div>
        </div>

        {/* Family chain */}
        {member.family_id ? (
          <div className="card">
            <div className="card-header"><h3>🏠 My Family</h3></div>
            <div className="card-pad">
              <FamilyChain familyId={member.family_id} familyName={member.families?.name} currentMemberId={member.id} />
            </div>
          </div>
        ) : (
          <div className="card card-pad">
            <div style={{fontSize:11,fontWeight:700,color:'var(--text3)',textTransform:'uppercase',letterSpacing:.5,marginBottom:10}}>📖 Word of the Week</div>
            {word ? (
              <>
                <div style={{fontStyle:'italic',lineHeight:1.7,marginBottom:8,fontSize:13}}>"{word.scripture}"</div>
                <div style={{fontWeight:700,color:'var(--red-dark)'}}>— {word.reference}</div>
                {word.reflection && <div style={{fontSize:12,color:'var(--text3)',marginTop:8}}>{word.reflection}</div>}
              </>
            ) : <div style={{color:'var(--text3)',fontSize:13}}>No word published this week.</div>}
          </div>
        )}
      </div>

      {/* Recent contributions + notices */}
      <div className="grid-2">
        <div className="card">
          <div className="card-header"><h3>Recent Contributions</h3><button className="btn btn-outline btn-sm" onClick={()=>navigate('/member/financials')}>View All</button></div>
          {contributions.slice(0,5).map(c=>(
            <div key={c.id} className="flex-between" style={{padding:'10px 16px',borderBottom:'1px solid var(--border)'}}>
              <div>
                <div style={{fontWeight:600,fontSize:13}}>{c.type}</div>
                <div style={{fontSize:11,color:'var(--text3)'}}>{fmtDate(c.date)}{c.notes?` · ${c.notes}`:''}</div>
              </div>
              <div style={{fontWeight:700,color:'var(--blue-dark)'}}>{fmt(c.amount)}</div>
            </div>
          ))}
          {!contributions.length && <div style={{textAlign:'center',padding:20,color:'var(--text3)',fontSize:13}}>No contributions recorded yet</div>}
        </div>

        <div className="card">
          <div className="card-header"><h3>📢 Notices</h3></div>
          <div className="card-pad">
            {notices.map(n=>(
              <div key={n.id} style={{marginBottom:14,paddingBottom:14,borderBottom:'1px solid var(--border)'}}>
                <div style={{fontWeight:700,fontSize:13}}>{n.title}</div>
                <div style={{fontSize:12,color:'var(--text2)',marginTop:3,lineHeight:1.6}}>{n.body}</div>
                <div style={{fontSize:11,color:'var(--text3)',marginTop:4}}>{fmtDate(n.created_at)}</div>
              </div>
            ))}
            {!notices.length && <div style={{color:'var(--text3)',fontSize:13}}>No notices at this time.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
